import { CloudUploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
    Button,
    Empty,
    Flex,
    Image,
    List,
    notification,
    Tooltip,
    Typography,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { displayDateFormat } from "tm-shared/constants";
import { formatBytes } from "tm-shared/utils";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { photoStorage, StoredPhoto } from "@/Services/PhotoStorage";
import {
    setNextPhotoUploadRetryAt,
    setPhotoUploadRetryCandidatesCount,
} from "@/store/slices/offline";
import { handleDownloadFile } from "@/utils/file/handleDownloadFile";
import { isOnline } from "@/utils/network/isOnline";
import { isNeedRetry } from "@/utils/photo/photoUploadRetry";

import { StyledTag } from "../../Styled";

const MAX_ALLOWED_LATENCY_MS = 2000;

export const PhotoList = () => {
    const { offlineMode, requestQueueStatus } = useAppSelector(
        (state) => state.offline,
    );
    const dispatch = useAppDispatch();

    const [dbPhotos, setDbPhotos] = useState<StoredPhoto[]>([]);
    const [activePhoto, setActivePhoto] = useState<StoredPhoto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const candidates = useMemo(() => dbPhotos.filter(isNeedRetry), [dbPhotos]);

    useEffect(() => {
        let cancelled = false;

        void (async () => {
            try {
                setLoading(true);
                setError("");

                const photos = await photoStorage.getAllPhotos();
                if (cancelled) return;

                setDbPhotos(photos);
                dispatch(
                    setPhotoUploadRetryCandidatesCount(
                        photos.filter(isNeedRetry).length,
                    ),
                );
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    setError("Не вдалося отримати фото із бази даних");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    const handleUploadAllStub = useCallback(async () => {
        const isGoodLatency = await isOnline(MAX_ALLOWED_LATENCY_MS);
        if (!isGoodLatency) {
            notification.warning({
                message: "Завантаження фото тимчасово зупинено",
                description:
                    "Мережа відповідає занадто повільно, тому ми зупинили відправку, щоб уникнути помилок. Відновіть інтернет і повторіть спробу.",
            });

            return;
        }
        dispatch(
            setNextPhotoUploadRetryAt({
                time: dayjs().toISOString(),
                ignoreBackoff: true,
            }),
        );
    }, [dispatch]);

    return (
        <Flex vertical gap={12}>
            {candidates.length > 0 && (
                <Flex align="center" justify="space-between">
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        Фото в черзі: {candidates.length}
                    </Typography.Title>

                    <Button
                        type="primary"
                        icon={<CloudUploadOutlined />}
                        disabled={offlineMode || requestQueueStatus.isRunning}
                        onClick={handleUploadAllStub}
                    >
                        Завантажити всі
                    </Button>
                </Flex>
            )}

            {error && <Typography.Text type="danger">{error}</Typography.Text>}

            {candidates.length === 0 ? (
                <Empty description="Черга порожня — немає фото для завантаження" />
            ) : (
                <Image.PreviewGroup
                    preview={{
                        toolbarRender: () => (
                            <Button
                                type="primary"
                                disabled={!activePhoto}
                                onClick={() => {
                                    if (!activePhoto) return;
                                    handleDownloadFile(
                                        activePhoto.base64,
                                        activePhoto.name,
                                    );
                                }}
                            >
                                Скачати
                            </Button>
                        ),
                    }}
                >
                    <List
                        loading={loading}
                        itemLayout="horizontal"
                        dataSource={candidates}
                        renderItem={(p) => {
                            const dateText = dayjs(p.createdAt).format(
                                displayDateFormat.dateTime,
                            );

                            const lastTryText = p.lastUploadAt
                                ? dayjs(p.lastUploadAt).format(
                                      displayDateFormat.dateTime,
                                  )
                                : "---";

                            return (
                                <List.Item>
                                    <List.Item.Meta
                                        avatar={
                                            <Image
                                                width={72}
                                                height={72}
                                                src={p.base64}
                                                style={{
                                                    objectFit: "cover",
                                                    borderRadius: 10,
                                                    cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                    setActivePhoto(p)
                                                }
                                            />
                                        }
                                        title={
                                            <Flex vertical gap={4}>
                                                <Typography.Text
                                                    strong
                                                    ellipsis
                                                >
                                                    {p.name}
                                                </Typography.Text>

                                                <Flex wrap gap={6}>
                                                    <StyledTag>
                                                        {p.type}
                                                    </StyledTag>
                                                    <StyledTag>
                                                        {formatBytes(p.size)}
                                                    </StyledTag>

                                                    <Tooltip
                                                        title={p.lastError}
                                                    >
                                                        <StyledTag color="orange">
                                                            спроб:{" "}
                                                            {p.uploadAttempts}{" "}
                                                            <InfoCircleOutlined />
                                                        </StyledTag>
                                                    </Tooltip>
                                                </Flex>
                                            </Flex>
                                        }
                                        description={
                                            <Flex vertical gap={2}>
                                                <Typography.Text type="secondary">
                                                    Дата створення: {dateText}
                                                </Typography.Text>
                                                <Typography.Text type="secondary">
                                                    Остання спроба:{" "}
                                                    {lastTryText}
                                                </Typography.Text>
                                            </Flex>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />
                </Image.PreviewGroup>
            )}
        </Flex>
    );
};
